"use client";

import { useState, useEffect } from "react";
import { storyblokEditable, StoryblokComponent } from "@storyblok/react/rsc";

import {
  Carousel,
  CarouselContent,
  CarouselItem,
  GalleryNavPrevious,
  GalleryNavNext,
  type CarouselApi,
} from "@/components/ui/carousel";
import { GridContainer } from "@/components/layout/GridContainer";
import { cn } from "@/lib/utils";
import type { TopicSummaryGalleryStoryblok } from "@/component-types-sb";

type TopicSummaryGalleryProps = {
  blok: TopicSummaryGalleryStoryblok;
};

const TopicSummaryGallery = ({ blok }: TopicSummaryGalleryProps) => {
  const [api, setApi] = useState<CarouselApi>();
  const [activeIndex, setActiveIndex] = useState(0);
  const [isSettled, setIsSettled] = useState(false);

  const cards = blok.cards || [];

  // Remove the init slide-in transition after it completes
  // so it doesn't interfere with drag/scroll behavior
  useEffect(() => {
    if (!api) return;

    const timer = setTimeout(() => setIsSettled(true), 600);
    return () => clearTimeout(timer);
  }, [api]);

  // Sync active badge with carousel scroll position
  useEffect(() => {
    if (!api) return;

    const onSelect = () => setActiveIndex(api.selectedScrollSnap());
    api.on("select", onSelect);

    return () => {
      api.off("select", onSelect);
    };
  }, [api]);

  const onBadgeClick = (index: number) => {
    api?.scrollTo(index);
  };

  if (cards.length === 0) return null;

  return (
    <div {...storyblokEditable(blok)} className="w-full">
      <section className="bg-gray-10 py-8 flex flex-col gap-4 w-full items-center rounded-3xl md:rounded-none">
        {blok.title && (
          <h2 className="text-headline-xl text-black text-center mb-2">
            {blok.title}
          </h2>
        )}

        {/* Badge pills */}
        <div className="flex flex-wrap gap-[2px] items-center justify-center w-full px-4 md:px-8">
          {cards.map((card, index) => (
            <button
              key={card._uid}
              onClick={() => onBadgeClick(index)}
              className={cn(
                "px-5 py-3 rounded-full text-button transition-all",
                index === activeIndex
                  ? "bg-white text-black"
                  : "bg-gray-25 text-black opacity-60",
              )}
            >
              {card.badge_label || card.title}
            </button>
          ))}
        </div>

        {/* Cards carousel */}
        <GridContainer className="!px-0">
          <div className="col-span-12 lg:col-span-10 lg:col-start-2">
            <Carousel
              setApi={setApi}
              opts={{
                align: "center",
                loop: false,
                dragFree: false,
                watchResize: true,
                containScroll: false,
              }}
              className="w-full relative"
            >
              <GalleryNavPrevious />
              <CarouselContent
                className={cn(
                  "ml-0 pl-4 md:pl-8 lg:pl-0 items-stretch",
                  !isSettled && "transition-transform duration-500 ease-out",
                )}
              >
                {cards.map((card) => (
                  <CarouselItem
                    key={card._uid}
                    className="pl-4 first:lg:pl-0 basis-auto h-auto"
                  >
                    <StoryblokComponent blok={card} />
                  </CarouselItem>
                ))}
                <div className="shrink-0 w-4 md:w-8 lg:w-0" />
              </CarouselContent>
              <GalleryNavNext />
            </Carousel>
          </div>
        </GridContainer>
      </section>
    </div>
  );
};

export default TopicSummaryGallery;
